import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { UploadMetersResponse } from "@/api/districts";
import { useUploadMeters } from "@/api/districts";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { buildMeterImportCsv, parseMeterImportCsv } from "@/lib/csv-parser";
import { ImportMetersDropzone } from "./import-meters-dropzone";
import { ImportMetersEditor } from "./import-meters-editor";
import { ImportMetersResult } from "./import-meters-result";
import type { MeterImportRow } from "./import-meters-types";
import { makeEmptyRow, makeRowFromParsed } from "./import-meters-types";

type Step = "dropzone" | "editor" | "result";

interface ImportMetersDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	districtId: number | null;
}

export function ImportMetersDialog({
	open,
	onOpenChange,
	districtId,
}: ImportMetersDialogProps) {
	const { t } = useTranslation();
	const [step, setStep] = useState<Step>("dropzone");
	const [rows, setRows] = useState<MeterImportRow[]>([]);
	const [parseError, setParseError] = useState<string | null>(null);
	const [response, setResponse] = useState<UploadMetersResponse | null>(null);
	const [pendingDiscard, setPendingDiscard] = useState<"close" | "back" | null>(
		null,
	);
	const upload = useUploadMeters();

	function resetAll() {
		setStep("dropzone");
		setRows([]);
		setParseError(null);
		setResponse(null);
	}

	useEffect(() => {
		if (open) resetAll();
	}, [open]);

	function tryClose() {
		if (step === "editor" && rows.length > 0) setPendingDiscard("close");
		else onOpenChange(false);
	}

	function tryBack() {
		if (rows.length > 0) setPendingDiscard("back");
		else resetAll();
	}

	function handleDiscardConfirm() {
		if (pendingDiscard === "back") {
			resetAll();
			setPendingDiscard(null);
		} else {
			setPendingDiscard(null);
			onOpenChange(false);
		}
	}

	function handleFile(file: File) {
		setParseError(null);
		file
			.text()
			.then((txt) => {
				const result = parseMeterImportCsv(txt);
				if (!result.ok) {
					setParseError(result.error);
					return;
				}
				setRows(result.rows.map(makeRowFromParsed));
				setStep("editor");
			})
			.catch(() => setParseError("read_failed"));
	}

	function handleStartBlank() {
		setRows([makeEmptyRow()]);
		setStep("editor");
	}

	function handleSubmit() {
		if (!districtId) {
			toast.error(t("meterImport.noDistrict"));
			return;
		}
		const csv = buildMeterImportCsv(rows);
		const blob = new Blob([csv], { type: "text/csv" });
		const file = new File([blob], "meters-import.csv", { type: "text/csv" });
		upload.mutate(
			{ district_id: districtId, file },
			{
				onSuccess: (data) => {
					setResponse(data);
					setStep("result");
				},
				onError: () => {
					toast.error(t("meterImport.error"));
				},
			},
		);
	}

	return (
		<>
			<Dialog
				open={open}
				onOpenChange={(o) => (o ? onOpenChange(true) : tryClose())}
			>
				<DialogContent className="flex max-h-[90vh] flex-col gap-0 p-0">
					<DialogHeader className="p-4 pb-2">
						<DialogTitle>{t("meterImport.title")}</DialogTitle>
						<DialogDescription>
							{t(`meterImport.desc.${step}`)}
						</DialogDescription>
					</DialogHeader>
					{step === "dropzone" && (
						<ImportMetersDropzone
							onFile={handleFile}
							onStartBlank={handleStartBlank}
							parseError={parseError}
						/>
					)}
					{step === "editor" && (
						<ImportMetersEditor
							rows={rows}
							onRowsChange={setRows}
							onSubmit={handleSubmit}
							onBack={tryBack}
							isPending={upload.isPending}
						/>
					)}
					{step === "result" && response && (
						<ImportMetersResult
							response={response}
							onClose={() => onOpenChange(false)}
						/>
					)}
				</DialogContent>
			</Dialog>

			<AlertDialog
				open={pendingDiscard !== null}
				onOpenChange={(o) => !o && setPendingDiscard(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("meterImport.confirmDiscardTitle")}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t("meterImport.confirmDiscardDesc")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>
							{t("meterImport.keepEditing")}
						</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={handleDiscardConfirm}
						>
							{t("meterImport.discard")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}
